// Wettkampfart = Waffenart + Anschlagsart einer Klassengruppe.
//
// Diese Kombination hat die frühere Disziplin als Gruppierungs- und
// Vergleichsschlüssel abgelöst. Sie entscheidet darüber,
//   * welche Mannschaften eines Vereins sich eine Nummernfolge teilen,
//   * ob ein Schütze in zwei Mannschaften doppelt starten würde,
//   * welcher Vorjahresschnitt zu einer Meldung passt.
//
// Die Schusszahl gehört bewusst NICHT zum Schlüssel: Gruppen, die sich nur
// darin unterscheiden (früher die Disziplinen A = 30 und B = 15 Schuss),
// gelten weiterhin als dieselbe Wettkampfart. Für die Übernahme des
// Vorjahresschnitts wird stattdessen über das Schusszahl-Verhältnis skaliert.

/**
 * Schlüssel einer Wettkampfart. Akzeptiert Klassengruppen ebenso wie
 * Auswertungszeilen, die die Werte unter `wettkampfart` mitliefern.
 * Liefert '' wenn weder Waffen- noch Anschlagsart gesetzt sind.
 */
export function getWettkampfartKey(entity) {
  const source = entity?.wettkampfart ?? entity ?? {};
  const waffenartId = source?.waffenartId;
  const anschlagsartId = source?.anschlagsartId;
  if (waffenartId == null && anschlagsartId == null) return '';
  return `${waffenartId ?? ''}|${anschlagsartId ?? ''}`;
}

/**
 * Schlüssel inklusive Schusszahl. Für Fälle, in denen nur ein exakter Treffer
 * zählt – etwa bei der Übernahme aus der Vorsaison, wo dieselben Klassengruppen
 * kopiert werden und deshalb nichts umgerechnet werden muss.
 */
export function getWettkampfartSchusszahlKey(entity) {
  const key = getWettkampfartKey(entity);
  if (!key) return '';
  return `${key}|${getSchusszahl(entity) ?? ''}`;
}

/** Schusszahl einer Klassengruppe oder Auswertungszeile. */
export function getSchusszahl(entity) {
  const raw = entity?.schusszahl ?? entity?.wettkampfart?.schusszahl;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Rechnet einen Schnitt von einer Schusszahl auf eine andere um.
 * Fehlt eine der beiden Schusszahlen, wird der Wert unverändert übernommen –
 * dann ist keine sinnvolle Skalierung möglich.
 */
export function skaliereAufSchusszahl(wert, quellSchusszahl, zielSchusszahl) {
  const n = Number(wert);
  if (!Number.isFinite(n)) return null;
  const von = Number(quellSchusszahl);
  const nach = Number(zielSchusszahl);
  if (!Number.isFinite(von) || von <= 0 || !Number.isFinite(nach) || nach <= 0) return n;
  return n * (nach / von);
}

/**
 * Lesbare Bezeichnung einer Wettkampfart, z. B. "LG freistehend".
 * Bevorzugt die Kürzel, fällt sonst auf die Beschreibung zurück.
 */
export function formatWettkampfart(entity) {
  const waffenart = entity?.waffenart;
  const anschlagsart = entity?.anschlagsart;
  const parts = [
    waffenart?.kuerzel || waffenart?.beschreibung || entity?.wettkampfart?.waffenartKuerzel,
    anschlagsart?.kuerzel || anschlagsart?.beschreibung || entity?.wettkampfart?.anschlagsartKuerzel,
  ].filter(Boolean);
  return parts.join(' ');
}

/**
 * Anzeigename einer Klassengruppe. Der in den Klasseneinstellungen gepflegte
 * Name hat Vorrang; ohne ihn wird er aus Altersklasse und Wettkampfart gebaut.
 */
export function buildGruppenLabel(gruppe) {
  const name = String(gruppe?.name ?? '').trim();
  if (name) return name;

  const parts = [gruppe?.altersklasse?.name, formatWettkampfart(gruppe)].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  return `Gruppe ${gruppe?.klassengruppenId ?? '?'}`;
}

/**
 * Anzeigename einer Klasse: Klassengruppe plus zweistellige Rangfolge,
 * z. B. "Schützen D LG freistehend 30 01".
 */
export function buildKlassenName(klasse, gruppe) {
  const parts = [];

  const name = String(gruppe?.name ?? '').trim();
  if (name) {
    parts.push(name);
  } else {
    if (gruppe?.altersklasse?.name) parts.push(gruppe.altersklasse.name);
    const gs = (gruppe?.geschlecht ?? '').toLowerCase();
    if (gs === 'weiblich' || gs === 'w') parts.push('D');
    else if (gs === 'männlich' || gs === 'm') parts.push('H');
    const art = formatWettkampfart(gruppe);
    if (art) parts.push(art);
  }

  if (klasse?.rangfolge != null) parts.push(String(Number(klasse.rangfolge)).padStart(2, '0'));
  return parts.join(' ');
}
